import { type ReactNode } from 'react'
import { makeAutoObservable } from 'mobx'
import { type ICardFilter, Typography } from 'shared/ui'
import { numberFormat } from 'shared/lib'
import { type ITooltipPlacementProps } from 'shared/ui/Tooltip/ui/Tooltip'
import type { IContactsStatisticFilter } from 'entities/ContactStatistic'
import type { IEntitiesStore } from 'entities/EntitiesExport'

type BroadcastFilterConfigKey = Exclude<IContactsStatisticFilter, 'subscribed'>

type IStatisticProps = {
  contacts_count?: number
  is_track_conversion?: boolean
  total_count: number | null
  in_progress_count: number | null
  quiet_hours_count: number | null
  success_count: number | null
  skipped_count: number | null
  reply_count: number | null
  no_replies_count: number | null
  clicks_count: number | null
  conversions_count: number | null
  revenue_cost: string | number | null
  failed_count: number | null
  unsubscribed_count: number | null
}

interface IFilterConfig {
  title?: string
  tooltipLabel?: ReactNode
  tooltipPlacement?: ITooltipPlacementProps['placement']
}

interface IFilterConfigWithTitle extends IFilterConfig {
  title: string
}

export class BroadcastBaseStatistic {
  contacts_count: number | null = null
  is_track_conversion?: boolean
  total_count: number | null = 0
  in_progress_count: number | null = null
  quiet_hours_count: number | null = null
  success_count: number | null = null
  skipped_count: number | null = null
  reply_count: number | null = null
  no_replies_count: number | null = null
  clicks_count: number | null = null
  conversions_count: number | null
  revenue_cost: string | number | null
  failed_count: number | null = null
  unsubscribed_count: number | null = null

  constructor(
    readonly entities: IEntitiesStore,
    {
      contacts_count = 0,
      is_track_conversion,
      total_count = 0,
      in_progress_count,
      quiet_hours_count,
      success_count,
      skipped_count,
      reply_count,
      no_replies_count,
      clicks_count,
      conversions_count,
      revenue_cost,
      failed_count,
      unsubscribed_count,
    }: IStatisticProps
  ) {
    this.contacts_count = contacts_count
    this.is_track_conversion = is_track_conversion
    this.total_count = total_count
    this.in_progress_count = in_progress_count
    this.quiet_hours_count = quiet_hours_count
    this.success_count = success_count
    this.skipped_count = skipped_count
    this.reply_count = reply_count
    this.no_replies_count = no_replies_count
    this.clicks_count = clicks_count
    this.conversions_count = conversions_count
    this.revenue_cost = revenue_cost
    this.failed_count = failed_count
    this.unsubscribed_count = unsubscribed_count
    makeAutoObservable(this)
  }

  get in_progress_percent() {
    if (this.in_progress_count && this.total_count) {
      return Math.round((this.in_progress_count / this.total_count) * 100)
    }
    return 0
  }
  get quiet_hours_percent() {
    if (this.quiet_hours_count && this.total_count) {
      return Math.round((this.quiet_hours_count / this.total_count) * 100)
    }
    return 0
  }
  get success_percent() {
    if (this.success_count && this.total_count) {
      return Math.round((this.success_count / this.total_count) * 100)
    }
    return 0
  }
  get skipped_percent() {
    if (this.skipped_count && this.total_count) {
      return Math.round((this.skipped_count / this.total_count) * 100)
    }
    return 0
  }
  get reply_percent() {
    if (this.reply_count && this.total_count) {
      return Math.round((this.reply_count / this.total_count) * 100)
    }
    return 0
  }
  get no_replies_percent() {
    if (this.no_replies_count && this.total_count) {
      return Math.round((this.no_replies_count / this.total_count) * 100)
    }
    return 0
  }
  get clicks_percent() {
    if (this.clicks_count && this.total_count) {
      return Math.round((this.clicks_count / this.total_count) * 100)
    }
    return 0
  }
  get conversions_percent() {
    if (this.conversions_count && this.total_count) {
      return Math.round((this.conversions_count / this.total_count) * 100)
    }
    return 0
  }
  get failed_percent() {
    if (this.failed_count && this.total_count) {
      return Math.round((this.failed_count / this.total_count) * 100)
    }
    return 0
  }
  get unsubscribed_percent() {
    if (this.unsubscribed_count && this.total_count) {
      return Math.round((this.unsubscribed_count / this.total_count) * 100)
    }
    return 0
  }

  get filters(): ICardFilter<IContactsStatisticFilter>[] {
    const entities = this.entities
    const configs: Record<BroadcastFilterConfigKey, IFilterConfigWithTitle> = {
      sent_to: {
        title: 'Total',
        tooltipLabel: 'Broadcast contacts',
      },
      in_progress: {
        title: 'In progress',
        tooltipLabel: ['Contacts who queued', <br key={'br'} />, 'to receive the message'],
      },
      quiet_hours: {
        title: 'Quiet hours',
        tooltipLabel: ['Contacts whose messages', <br key={'br'} />, 'are delayed by quiet hours'],
      },
      success: {
        title: 'Delivered',
        tooltipLabel: ['Contacts who received', <br key={'br'} />, 'the message'],
      },
      skipped: {
        title: 'Stopped by user',
        tooltipLabel: [
          'This was canceled during sending',
          <br key={'br'} />,
          <Typography
            ariaLabel={'desc'}
            key={'desc'}
            tag={'div'}
            variant={'body-sm-regular'}
            color={'var(--content-primary-tertiary-inverted)'}
            marginTop={2}
          >
            Before canceling, it was sent to{' '}
            {numberFormat({
              value: this.total_count || 0,
            })}{' '}
            of <br />
            {numberFormat({
              value: this.contacts_count || 0,
            })}{' '}
            eligible contacts.
          </Typography>,
        ],
      },
      replied: {
        title: 'Replies',
        tooltipLabel: ['Contacts who replied', <br key={'br'} />, 'to the message'],
      },
      no_replies: {
        title: 'No replies',
        tooltipLabel: ['Contacts who didn’t reply', <br key={'br'} />, 'to the message'],
      },
      clicked: {
        title: 'Clicks',
        tooltipLabel: ['Contacts who clicked', <br key={'br'} />, 'on the link(s)'],
      },
      conversions: {
        title: 'Conversions',
        tooltipLabel: 'Contacts who converted',
      },
      revenue: {
        title: 'Revenue',
        tooltipLabel: ['Revenue generated by the', <br key={'br'} />, 'contacts who converted'],
      },
      failed: {
        title: 'Undelivered',
        tooltipLabel: ['Contacts who didnn’t', <br key={'br'} />, 'received the message'],
      },
      opted_out: {
        title: 'Unsubscribed',
        tooltipLabel: 'Contacts who opted-out',
      },
    }

    const quietHoursFilter: ICardFilter<IContactsStatisticFilter> | null = entities
      ?.organizationStore.quietHoursSettings?.is_active
      ? {
          key: 'quiet_hours',
          title: configs.quiet_hours.title,
          amount: this.quiet_hours_count ?? 0,
          percentage: this.quiet_hours_percent,
          tooltipProps: {
            label: configs.quiet_hours.tooltipLabel,
            placement: configs.quiet_hours.tooltipPlacement || 'top',
          },
        }
      : null

    const filters: ICardFilter<IContactsStatisticFilter>[] = [
      {
        key: 'sent_to',
        title: configs.sent_to.title,
        amount: this.total_count,
        tooltipProps: {
          label: configs.sent_to.tooltipLabel,
          placement: configs.sent_to.tooltipPlacement || 'top',
        },
      },
      {
        key: 'in_progress',
        title: configs.in_progress.title,
        amount: this.in_progress_count,
        percentage: this.in_progress_percent,
        tooltipProps: {
          label: configs.in_progress.tooltipLabel,
          placement: configs.in_progress.tooltipPlacement || 'top',
        },
      },
      ...(quietHoursFilter ? [quietHoursFilter] : []),
      {
        key: 'success',
        title: configs.success.title,
        amount: this.success_count,
        percentage: this.success_percent,
        tooltipProps: {
          label: configs.success.tooltipLabel,
          placement: configs.success.tooltipPlacement || 'top',
        },
      },
      {
        key: 'skipped',
        title: configs.skipped.title,
        amount: this.skipped_count,
        percentage: this.skipped_percent,
        tooltipProps: {
          label: configs.skipped.tooltipLabel,
          placement: configs.skipped.tooltipPlacement || 'top',
        },
      },
      {
        key: 'replied',
        title: configs.replied.title,
        amount: this.reply_count,
        percentage: this.reply_percent,
        tooltipProps: {
          label: configs.replied.tooltipLabel,
          placement: configs.replied.tooltipPlacement || 'top',
        },
      },
      {
        key: 'no_replies',
        title: configs.no_replies.title,
        amount: this.no_replies_count,
        percentage: this.no_replies_percent,
        tooltipProps: {
          label: configs.no_replies.tooltipLabel,
          placement: configs.no_replies.tooltipPlacement || 'top',
        },
      },
      {
        key: 'clicked',
        title: configs.clicked.title,
        amount: this.clicks_count,
        percentage: this.clicks_percent,
        tooltipProps: {
          label: configs.clicked.tooltipLabel,
          placement: configs.clicked.tooltipPlacement || 'top',
        },
      },
      {
        key: 'conversions',
        title: configs.conversions.title,
        amount: this.conversions_count,
        percentage: this.conversions_percent,
        tooltipProps: {
          label: configs.conversions.tooltipLabel,
          placement: configs.conversions.tooltipPlacement || 'top',
        },
      },
      {
        key: 'revenue',
        title: configs.revenue.title,
        amountContent: this.revenue_cost,
        tooltipProps: {
          label: configs.revenue.tooltipLabel,
          placement: configs.revenue.tooltipPlacement || 'top',
        },
      },
      {
        key: 'failed',
        title: configs.failed.title,
        amount: this.failed_count,
        percentage: this.failed_percent,
        tooltipProps: {
          label: configs.failed.tooltipLabel,
          placement: configs.failed.tooltipPlacement || 'top',
        },
      },
      {
        key: 'opted_out',
        title: configs.opted_out.title,
        amount: this.unsubscribed_count,
        percentage: this.unsubscribed_percent,
        tooltipProps: {
          label: configs.opted_out.tooltipLabel,
          placement: configs.opted_out.tooltipPlacement || 'top',
        },
      },
    ]

    return filters.filter((filter) => filter.key !== 'in_progress' || !!filter.amount)
  }
}
